// Code/src/Service/ProfileService.ts
// Profile-related API operations
import {
  getWithAuth,
  postWithAuth,
  deleteWithAuth,
  putWithAuth,
} from "./InitializeService";
import { assertNotImpersonating } from "../Utility/devImpersonation";
import type {
  IAddListItemsProps,
  IGetListItemsProps,
  IGetTermsProps,
  IUpdateListItemsProps,
  IUpdateUserProfileProps,
} from "./IUserProfile";

/**
 * Get user profile for the current user
 * @param loginName - User's login name
 */
export const getUserProfileForCurrentUser = (loginName: string) => {
  return getWithAuth(
    `sharepoint/userProfiles?userId=${loginName}&siteUrl=https://sharepoint.teampbs.com`,
  );
};

/**
 * Get user profile by email
 * @param loginName - User's login name
 */

export const getUsersFullName = (loginNames: string[]) => {
  return postWithAuth(`userProfiles`, {
    dfcColumns: ["Full Name"],
    ntNames: loginNames,
    pageSize: 100,
  });
};

/**
 * Update user profile for the current user
 * @param loginName - User's login name
 * @param updateUserProfile - Profile properties to update
 */
export const updateUserProfileForCurrentUser = (
  loginName: string,
  updateUserProfile: IUpdateUserProfileProps,
) => {
  return postWithAuth(
    `sharepoint/userprofiles?userId=POSITIVE\\${loginName}`,
    updateUserProfile,
  );
};

/**
 * Add items to a SharePoint list
 * @param addListItem - Parameters for adding list items
 */

export const addListItem = (addListItem: IAddListItemsProps) => {
  return postWithAuth(`sharepoint/lists/items`, addListItem);
};

/**
 * Get SharePoint list items based on query parameters
 * @param getListItems - List query parameters
 */

export const getListItems = (getListItems: IGetListItemsProps) => {
  return postWithAuth(`sharepoint/lists/query`, getListItems);
};

/**
 * Update SharePoint list items
 * @param updateListItems - List update parameters
 */
export const updateListItems = (updateListItems: IUpdateListItemsProps) => {
  return putWithAuth(`sharepoint/lists/items/filter`, updateListItems);
};

/**
 * Delete a SharePoint list item
 * @param itemId - ID of the item to delete
 * @param listName - Name of the list
 * @param userEmail - Email of the user performing the deletion
 * @returns
 */

export const deleteListItem = (
  itemId: number,
  listName: string,
  userEmail: string,
) => {
  return deleteWithAuth(
    `sharepoint/lists/items/${itemId}?listName=${listName}&siteUrl=https://sharepoint.teampbs.com&user=${userEmail}`,
  );
};

/**
 * Get PBS region serviced based on user region
 * @param userRegion
 * @returns
 */

export const getPBSRegionServiced = (userRegion: any) => {
  return getWithAuth(
    `regionserviced?$filter=contains(Region, '${userRegion}')`,
  );
};

/**
 * Get all PBS regions
 */
export const getRegions = () => {
  return getWithAuth(`regions?$count=true`);
};

/**
 * Get all US states
 */
export const getStates = () => {
  return getWithAuth(`states?$orderby=Code`);
};

/**
 * Get taxonomy terms from SharePoint
 * @param getTerms - Term set parameters
 */
export const getTerms = (getTerms: IGetTermsProps) => {
  return postWithAuth(`sharepoint/taxonomy/terms`, getTerms);
};

/**
 * Get PBS roles from SharePoint
 */
export const getRoles = () => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "PBSRoles",
    camlQuery:
      "<View><Query><Where><Eq><FieldRef Name='Obsolete'/><Value Type='Boolean'>false</Value></Eq></Where></Query></View>",
    rowLimit: 100,
  });
};

/**
 * Get PBS roles from SharePoint
 */
export const getCountries = () => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Countries",
    rowLimit: 100,
  });
};

/**
 * Get applicant information by login name
 * @param loginName - User's login name
 */
export const getApplicant = (loginName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Applicants",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='AssignedLoginName' /><Value Type='Text'>${loginName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 1,
  });
};

/**
 * Get staff member information by login name
 * @param loginName - User's login name
 */
export const getStaff = (loginName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Directory for Coordinators",
    camlQuery: `<View><Query><Where><Eq><FieldRef Name='NTName' /><Value Type='Text'>${loginName}</Value></Eq></Where></Query></View>`,
    rowLimit: 1,
  });
};

/**
 * Get education history for a user
 * @param fullName - User's full name
 */
export const getEducationHistory = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Schools and Education",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='PBSUser' LookupId='FALSE' /><Value Type='Text'>${fullName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 100,
  });
};

/**
 * Get work history for a user
 * @param fullName - User's full name
 */
export const getWorkHistory = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Work Experience",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='PBSUser' LookupId='FALSE' /><Value Type='Text'>${fullName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 100,
  });
};

/**
 * Get PDH (training history) for a user
 * @param fullName - User's full name
 */
export const getPDH = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com/introaba",
    listName: "User Trainings",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='PBSUser' LookupId='FALSE' /><Value Type='Text'>${fullName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    viewFields: [
      "CourseID_x003a_Course",
      "TrainingsCalendarID_x003a_Title",
      "EventDate",
      "Completed1",
      "Mandatory",
      "Instructor",
      "HasTimeBeenEntered",
      "TrainingsCalendarID_x003a_Start_",
      "TrainingsCalendarID_x003a_End_x0",
      "Created",
      "PBSUser",
    ],
    rowLimit: 1000,
  });
};

/**
 * Upload profile picture for the current user
 * @param imageBase64
 * @returns
 */

export const uploadProfilePicture = async (imageBase64: string) => {
  assertNotImpersonating("Profile picture upload");
  return postWithAuth(`me/profilepicture`, { base64Image: imageBase64 });
};

/**
 * Get profile videos for a user by login name
 * @param loginName - User's login name
 * @returns List of profile videos
 */

export const getProfileVideos = (loginName: string) => {
  return getWithAuth(`profilevideos?stafflogin=positive\\${loginName}`);
};

/**
 * Get senior director for a region
 * @param region - User's region
 * @returns Senior director information
 */

export const getSeniorDirector = (region: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Regions",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='Title' /><Value Type='Text'>${region}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 1,
  });
};

/**
 * Get universities for dropdown options
 * @returns List of universities
 */

export const getUniversities = () => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Universities",
    rowLimit: 500,
  });
};

/**
 *
 * @param routingNumber
 * @returns
 */

export const validateRoutingNumber = (routingNumber: any) => {
  return getWithAuth(`validations/routing-number?value=${routingNumber}`);
};

/**
 *
 * @param npi // National Provider Identifier - a unique 10-digit number assigned to healthcare providers in the US
 * @returns
 */

export const validateNPI = (npi: any) => {
  return getWithAuth(`validations/npi?value=${npi}`);
};

/**
 * Get W4 information for a user by full name
 * @param fullName - User's full name
 */
export const getW4 = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "w4",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='User1' /><Value Type='Text'>${fullName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 1,
  });
};

export const getProfilePicture = async () => {
  assertNotImpersonating("Profile picture");
  return getWithAuth(`me/profilepicture?size=M`, {
    responseType: "blob",
  });
};
